import { z } from 'zod';

// CSP: production disallows 'unsafe-eval', so Zod must not use its Function-based
// JIT compiler. This must run before any schema below is constructed.
z.config({ jitless: true });

const id = z.string().uuid();
const localDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const moneyMinor = z.number().int().safe();

export const accountSchema = z.object({
  id,
  name: z.string().trim().min(1).max(100),
  type: z.enum(['BANK', 'CASH', 'SAVINGS', 'CREDIT', 'OTHER']),
  openingBalanceMinor: moneyMinor,
  currency: z.string().length(3),
  description: z.string().max(500).default(''),
  active: z.boolean(),
  spendable: z.boolean(),
  protected: z.boolean(),
  minimumBalanceMinor: moneyMinor.nonnegative(),
});

export const transactionSchema = z
  .object({
    id,
    type: z.enum(['INCOME', 'EXPENSE', 'TRANSFER']),
    amountMinor: moneyMinor.positive(),
    transactionDate: localDate,
    accountId: id.nullable(),
    transferFromAccountId: id.nullable(),
    transferToAccountId: id.nullable(),
    categoryId: id.nullable(),
    budgetGroupId: id.nullable(),
    notes: z.string().max(1000),
  })
  .superRefine((value, context) => {
    if (value.type === 'TRANSFER') {
      if (!value.transferFromAccountId || !value.transferToAccountId) {
        context.addIssue({
          code: 'custom',
          message: 'Transfers require source and destination accounts',
        });
      }
      if (value.transferFromAccountId === value.transferToAccountId) {
        context.addIssue({ code: 'custom', message: 'Transfer accounts must differ' });
      }
    } else if (!value.accountId) {
      context.addIssue({ code: 'custom', message: 'Income and expenses require an account' });
    }
  });

export const backupSchema = z.object({
  format: z.literal('expense-assistant-backup'),
  version: z.literal(1),
  exportedAt: z.string().datetime({ offset: true }),
  currency: z.string().length(3),
  records: z.object({
    accounts: z.array(z.record(z.string(), z.unknown())),
    categories: z.array(z.record(z.string(), z.unknown())),
    budgetGroups: z.array(z.record(z.string(), z.unknown())),
    recurringRules: z.array(z.record(z.string(), z.unknown())),
    plannedEvents: z.array(z.record(z.string(), z.unknown())),
    transactions: z.array(z.record(z.string(), z.unknown())),
    settlements: z.array(z.record(z.string(), z.unknown())),
    debts: z.array(z.record(z.string(), z.unknown())),
    debtPayments: z.array(z.record(z.string(), z.unknown())),
    goals: z.array(z.record(z.string(), z.unknown())),
    goalAllocations: z.array(z.record(z.string(), z.unknown())),
    preferences: z.array(z.record(z.string(), z.unknown())),
  }),
});

export type FinancialBackup = z.infer<typeof backupSchema>;

export function validateBackup(value: unknown): FinancialBackup {
  return backupSchema.parse(value);
}
